import { BaseInput } from "shared/components/forms/BaseInput";
import { floatParser } from "shared/components/forms/BaseInput/parsers";
import { DateInput, Select, type SelectProps } from "shared/components";
import { type Register } from "shared/hooks";
import { useAuthContext } from "providers/Auth";
import { SectionCard } from "shared/app/SectionCard";
import { salePriceParser } from "./constants";

const moneyParser = floatParser({ maxDecimals: 2 });

const statusOptions = [
  { value: false, label: "Available" },
  { value: true, label: "Occupied" }
];

interface RentalInfoFormProps {
  register: Register;
  headerExtra?: React.ReactNode;
  footer?: React.ReactNode;
  canChangeAvailablebyDate?: boolean;
  canChangeRentAmount?: boolean;
}

export const RentalInfoForm = (props: RentalInfoFormProps) => {
  const {
    register,
    headerExtra,
    footer,
    canChangeAvailablebyDate = true,
    canChangeRentAmount = true
  } = props;

  const { pms, type } = useAuthContext();
  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;
  return (
    <SectionCard
      title="Rental information"
      headerExtra={headerExtra}
      footer={footer}
    >
      <div className="divide-y divide-alpha-40">
        <div className="grid grid-cols-2 gap-4 pb-4">
          {homeSiteRentEnabled ? (
            <>
              <div className="w-full">
                <BaseInput
                  id="homeRentAmount"
                  label="Home rent"
                  placeholder="1,500"
                  prefix="$"
                  parser={moneyParser}
                  required
                  disabled={!canChangeRentAmount}
                  value={register("home_rent_amount").value ?? null}
                  onChange={(val) => register("home_rent_amount").onChange(val)}
                  error={register("home_rent_amount").error}
                />
              </div>
              <div className="w-full">
                <BaseInput
                  id="siteRentAmount"
                  label="Site rent"
                  placeholder="1,500"
                  prefix="$"
                  parser={moneyParser}
                  required
                  disabled={!canChangeRentAmount}
                  value={register("site_rent_amount").value ?? null}
                  onChange={(val) => register("site_rent_amount").onChange(val)}
                  error={register("site_rent_amount").error}
                />
              </div>
            </>
          ) : (
            <div className="w-full">
              <BaseInput
                id="monthlyRentAmount"
                label="Rent amount"
                placeholder="1,500"
                prefix="$"
                parser={moneyParser}
                required
                disabled={!canChangeRentAmount}
                value={register("monthly_rent_amount").value ?? null}
                onChange={(val) =>
                  register("monthly_rent_amount").onChange(val)
                }
                error={register("monthly_rent_amount").error}
              />
            </div>
          )}
          <div className="w-full">
            <BaseInput
              id="salePrice"
              label="Sale price"
              placeholder="1,500"
              prefix="$"
              parser={salePriceParser}
              disabled={!canChangeRentAmount}
              value={register("sale_price").value ?? null}
              onChange={(val) => register("sale_price").onChange(val)}
              error={register("sale_price").error}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 pt-4">
          <div>
            <Select
              id="current-status"
              label="Current status"
              options={statusOptions}
              required
              value={register("occupied").value as boolean}
              onChange={
                register("occupied").onChange as SelectProps["onChange"]
              }
              isDisabled={!canChangeAvailablebyDate}
            />
          </div>

          <div>
            <DateInput
              id="available_at"
              label="Available by date"
              required
              readOnly={
                !register("occupied").value || !canChangeAvailablebyDate
              }
              {...register("available_at")}
            />
          </div>
        </div>
      </div>
    </SectionCard>
  );
};
